import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as dataService from '@/services/dataService'
import * as sb from '@/services/supabaseService'
import { getProjectCoaAccounts } from '@/services/chartOfAccountsService'
import { filterByAccessibleOrgs } from '@/services/authorizationService'
import { useAppStore } from '@/store/appStore'
import { useProjectEntitlementStore } from '@/config/projectEntitlements'
import { scopePortfoliosToCompany, scopeProjectsToCompany, demoCompanyPortfolioIds } from '@/types/demoCompanies'
import { collectPortfolioSubtreeIds } from '@/lib/portfolioTree'
import type { Portfolio } from '@/types/ppm'
import { getBudgetWbsSource, setBudgetWbsSource, getBudgetPlanNodes } from '@/services/budgetWbsSourceService'
import type { BudgetWbsSource, PlanSummary } from '@/lib/budgetWbsPlan'

/**
 * Sandbox org scope - narrow a flat row list to a single organization by
 * `organizationId`. Sandbox's parallel to Demo's company scope: when an org is
 * picked on the Sandbox homepage, portfolios/projects are filtered to it.
 * Returns the list unchanged when no org is scoped.
 *
 * Untagged rows (no `organizationId`) FAIL OPEN and stay visible, matching
 * `filterByAccessibleOrgs` in authorizationService, which deliberately keeps
 * unscoped/legacy rows visible. This used to fail CLOSED, and the mismatch
 * between the two gates silently hid whole portfolio subtrees: the org
 * localization backfill tagged `projects.organization_id` but never tagged
 * `portfolios`, so any user with an org pinned (i.e. everyone with a home org)
 * lost every untagged portfolio - and with it the portfolio-scoped project list
 * underneath. Org isolation still holds: a row tagged for another org is
 * excluded. Only genuinely unattributed rows pass.
 */
export function scopeRowsToOrg<T extends { organizationId?: string | null }>(
  rows: T[],
  orgId: string | null,
): T[] {
  if (!orgId) return rows
  return rows.filter((r) => !r.organizationId || r.organizationId === orgId)
}

/**
 * Returns the set of portfolio IDs that includes the selected portfolio
 * and all its descendants in the hierarchy. Used for pages that aggregate
 * data across the portfolio tree (projects, budget, risks, resources).
 */
export function usePortfolioScope() {
  const portfolio = useAppStore((s) => s.selectedPortfolio)
  const { data: portfolios } = usePortfolios()

  // Walk descendants by parentId, NOT childIds: child_ids is a denormalized
  // forward-link that drifts when a re-parent updates parent_id but not the
  // parent's child_ids array (the Gentex value-stream restructure did exactly
  // this — silently emptying this scope so parent-portfolio dashboards rolled
  // up zero projects). See lib/portfolioTree.
  return useMemo(
    () => collectPortfolioSubtreeIds(portfolios ?? [], portfolio?.id),
    [portfolio, portfolios],
  )
}

export function usePortfolios() {
  // ProjectTasks #78f Slice 3 - gate visible portfolios by the current
  // user's accessible-orgs set. GlobalAdmin sees everything; portfolio-
  // scoped users see only portfolios in orgs they have access to. Legacy
  // rows without an organizationId stay visible (fail-open) until the
  // backfill migration ships in a later phase.
  const currentUser = useAppStore((s) => s.currentUser)
  const currentCompanyId = useAppStore((s) => s.currentCompanyId)
  const activeOrganizationId = useAppStore((s) => s.activeOrganizationId)
  const appMode = useAppStore((s) => s.appMode)
  // Per-mode scope: Demo uses the demo-company scope; Sandbox + Production both read
  // the SINGLE active organization (activeOrganizationId — the over-arching org
  // scope; #115 P1 collapse 2026-06-26, sandbox no longer keys on a separate
  // sandboxOrgId). Dev/Quality stay organization-wide. Each scope is null outside
  // its mode so it no-ops (all data shows).
  const scopeCompanyId = appMode === 'demo' ? currentCompanyId : null
  // A project-restricted user (allowed_projects, 00541) is already scoped to their projects by the data layer, across organizations.
  const projectScopeKey = useProjectEntitlementStore((s) => (s.projectIds === null ? '_unrestricted' : s.projectIds.join(',')))
  const restricted = projectScopeKey !== '_unrestricted'
  const scopeOrgId = !restricted && (appMode === 'sandbox' || appMode === 'production') ? activeOrganizationId : null
  return useQuery({
    queryKey: ['portfolios', currentUser?.id ?? '_anon', scopeCompanyId ?? '_all', scopeOrgId ?? '_allOrgs', projectScopeKey],
    queryFn: async () => {
      const rows = await dataService.getPortfolios()
      const gated = !currentUser?.id
        ? rows
        : filterByAccessibleOrgs(
            rows as Array<Portfolio & { organizationId?: string | null; dataClass?: 'demo' | 'production' | null }>,
            currentUser.id,
            (p) => p.organizationId ?? null,
            (p) => p.dataClass ?? null,
          )
      // #67-lite: when a demo company is selected, show only its portfolio subtree.
      const companyScoped = scopePortfoliosToCompany(gated as Array<Portfolio & { parentId?: string | null }>, scopeCompanyId)
      // Sandbox: hide demo-company subtrees (demos live behind the demo profiles,
      // not the sandbox), then narrow to the picked organization (no-op when none).
      const sandboxFiltered = appMode === 'sandbox'
        ? (() => {
            const demoIds = demoCompanyPortfolioIds(companyScoped as Array<{ id: string; parentId?: string | null }>)
            return (companyScoped as Array<Portfolio & { id: string }>).filter((p) => !demoIds.has(p.id))
          })()
        : companyScoped
      return scopeRowsToOrg(sandboxFiltered as Array<Portfolio & { organizationId?: string | null }>, scopeOrgId)
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function usePortfolioHierarchy() {
  // ProjectTasks #78f Slice 3 - fetch the flat portfolio list (already
  // org-gated when we filter it below), then build the tree from the
  // accessible set. Filtering at the leaves of an already-built tree
  // would leave orphan parents pointing into inaccessible subtrees;
  // building from the filtered flat list keeps the tree well-formed.
  const currentUser = useAppStore((s) => s.currentUser)
  const currentCompanyId = useAppStore((s) => s.currentCompanyId)
  const activeOrganizationId = useAppStore((s) => s.activeOrganizationId)
  const appMode = useAppStore((s) => s.appMode)
  // Per-mode scope: Demo uses the demo-company scope; Sandbox + Production both read
  // the SINGLE active organization (activeOrganizationId — the over-arching org
  // scope; #115 P1 collapse 2026-06-26, sandbox no longer keys on a separate
  // sandboxOrgId). Dev/Quality stay organization-wide. Each scope is null outside
  // its mode so it no-ops (all data shows).
  const scopeCompanyId = appMode === 'demo' ? currentCompanyId : null
  // A project-restricted user (allowed_projects, 00541) is already scoped to their projects by the data layer, across organizations.
  const projectScopeKey = useProjectEntitlementStore((s) => (s.projectIds === null ? '_unrestricted' : s.projectIds.join(',')))
  const restricted = projectScopeKey !== '_unrestricted'
  const scopeOrgId = !restricted && (appMode === 'sandbox' || appMode === 'production') ? activeOrganizationId : null
  return useQuery({
    queryKey: ['portfolioHierarchy', currentUser?.id ?? '_anon', scopeCompanyId ?? '_all', scopeOrgId ?? '_allOrgs', projectScopeKey],
    queryFn: async () => {
      const flat = (await dataService.getPortfolios()) as Array<
        Portfolio & { organizationId?: string | null; dataClass?: 'demo' | 'production' | null }
      >
      const gated = currentUser?.id
        ? filterByAccessibleOrgs(
            flat,
            currentUser.id,
            (p) => p.organizationId ?? null,
            (p) => p.dataClass ?? null,
          )
        : flat
      // #67-lite: scope the flat list to the selected company's subtree BEFORE
      // building the tree, so the tree is well-formed (no orphan parents).
      // Sandbox: hide demo-company subtrees (reached via the demo profiles, not
      // the sandbox), then narrow to the picked organization before building.
      const companyScoped = scopePortfoliosToCompany(gated, scopeCompanyId)
      const sandboxFiltered = appMode === 'sandbox'
        ? (() => {
            const demoIds = demoCompanyPortfolioIds(companyScoped)
            return companyScoped.filter((p) => !demoIds.has(p.id))
          })()
        : companyScoped
      const accessible = scopeRowsToOrg(sandboxFiltered, scopeOrgId)
      const byId = new Map<string, Portfolio & { children: Portfolio[] }>()
      accessible.forEach((p) => byId.set(p.id, { ...p, children: [] }))
      const roots: Portfolio[] = []
      accessible.forEach((p) => {
        const node = byId.get(p.id)!
        if (p.parentId && byId.has(p.parentId)) {
          byId.get(p.parentId)!.children.push(node)
        } else {
          roots.push(node)
        }
      })
      return roots
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function useProjects(portfolioId?: string) {
  // ProjectTasks #78f Slice 3 - same accessible-orgs gate as
  // usePortfolios. Projects with no organizationId stay visible
  // (legacy rows).
  const currentUser = useAppStore((s) => s.currentUser)
  const currentCompanyId = useAppStore((s) => s.currentCompanyId)
  const activeOrganizationId = useAppStore((s) => s.activeOrganizationId)
  const appMode = useAppStore((s) => s.appMode)
  // Per-mode scope: Demo uses the demo-company scope; Sandbox + Production both read
  // the SINGLE active organization (activeOrganizationId — the over-arching org
  // scope; #115 P1 collapse 2026-06-26, sandbox no longer keys on a separate
  // sandboxOrgId). Dev/Quality stay organization-wide. Each scope is null outside
  // its mode so it no-ops (all data shows).
  const scopeCompanyId = appMode === 'demo' ? currentCompanyId : null
  // A project-restricted user (allowed_projects, 00541) is already scoped to their projects by the data layer, across organizations.
  const projectScopeKey = useProjectEntitlementStore((s) => (s.projectIds === null ? '_unrestricted' : s.projectIds.join(',')))
  const restricted = projectScopeKey !== '_unrestricted'
  const scopeOrgId = !restricted && (appMode === 'sandbox' || appMode === 'production') ? activeOrganizationId : null
  return useQuery({
    queryKey: ['projects', portfolioId, currentUser?.id ?? '_anon', scopeCompanyId ?? '_all', scopeOrgId ?? '_allOrgs', projectScopeKey],
    queryFn: async () => {
      const rows = await dataService.getProjects(portfolioId)
      let result = rows
      if (currentUser?.id) {
        type ScopedRow = { organizationId?: string | null; dataClass?: 'demo' | 'production' | null }
        const scoped = result as unknown as ScopedRow[]
        const filtered = filterByAccessibleOrgs(
          scoped,
          currentUser.id,
          (p) => p.organizationId ?? null,
          (p) => p.dataClass ?? null,
        )
        // Cast back to the original Project[] shape - filtering doesn't
        // mutate row contents, only the visible subset.
        result = filtered as unknown as typeof rows
      }
      // Company scope (mirrors usePortfolios): when a demo company is selected,
      // restrict to projects whose portfolio is in that company's subtree, so the
      // project list/counts match the chosen company instead of showing every
      // company's projects.
      if (scopeCompanyId) {
        const portfolios = await dataService.getPortfolios()
        result = scopeProjectsToCompany(
          result as unknown as Array<{ portfolioId?: string | null }>,
          portfolios as unknown as Array<{ id: string; parentId?: string | null }>,
          scopeCompanyId,
        ) as unknown as typeof result
      }
      // Sandbox: hide projects under demo-company subtrees (matching the sandbox
      // portfolio scope), then narrow to the picked organization so lists/counts
      // agree with the visible tree.
      if (appMode === 'sandbox') {
        const portfolios = await dataService.getPortfolios()
        const demoIds = demoCompanyPortfolioIds(
          portfolios as unknown as Array<{ id: string; parentId?: string | null }>,
        )
        result = (result as unknown as Array<{ portfolioId?: string | null }>).filter(
          (p) => !(p.portfolioId != null && demoIds.has(p.portfolioId)),
        ) as unknown as typeof result
        if (scopeOrgId) {
          result = scopeRowsToOrg(
            result as unknown as Array<{ organizationId?: string | null }>,
            scopeOrgId,
          ) as unknown as typeof result
        }
      }
      // Production: scope projects to the active organization (the over-arching org
      // layer), matching the portfolio scope so project lists/counts agree.
      if (appMode === 'production' && scopeOrgId) {
        result = scopeRowsToOrg(
          result as unknown as Array<{ organizationId?: string | null }>,
          scopeOrgId,
        ) as unknown as typeof result
      }
      return result
    },
    staleTime: 2 * 60 * 1000,
  })
}

// ProjectTasks #78f Slice 3b - Resource list with accessible-orgs gate.
// Centralizes the org-filter on resource queries so the 3 known callers
// (Resource Consumption page, MyWork PortalGrid, Resource Utilization
// report) all share the same gate without three separate
// implementations.
export function useResources() {
  const currentUser = useAppStore((s) => s.currentUser)
  return useQuery({
    queryKey: ['resources', currentUser?.id ?? '_anon'],
    queryFn: async () => {
      const rows = await dataService.getResources()
      if (!currentUser?.id) return rows
      type ScopedRow = { organizationId?: string | null; dataClass?: 'demo' | 'production' | null }
      const scoped = rows as unknown as ScopedRow[]
      const filtered = filterByAccessibleOrgs(
        scoped,
        currentUser.id,
        (r) => r.organizationId ?? null,
        (r) => r.dataClass ?? null,
      )
      return filtered as unknown as typeof rows
    },
    staleTime: 2 * 60 * 1000,
  })
}

export function useProject(projectId: string) {
  return useQuery({
    queryKey: ['project', projectId],
    queryFn: () => dataService.getProjectById(projectId),
    enabled: !!projectId,
  })
}

export function useWbsElements(projectId: string) {
  return useQuery({
    queryKey: ['wbs', projectId],
    queryFn: () => dataService.getWbsElements(projectId),
    enabled: !!projectId,
  })
}

export function useTasks(projectId: string | undefined) {
  return useQuery({
    queryKey: ['tasks', projectId],
    queryFn: () => dataService.getTasksByProject(projectId!),
    enabled: !!projectId,
    staleTime: 2 * 60 * 1000,
  })
}

export function useRisks(projectId?: string) {
  return useQuery({
    queryKey: ['risks', projectId],
    queryFn: () => dataService.getRisks(projectId),
    staleTime: 2 * 60 * 1000,
  })
}

/** A project with no portfolio - it can't be matched by any portfolio scope. */
export function isUnassignedToPortfolio(p: { portfolioId?: string | null }): boolean {
  return !p.portfolioId
}

/**
 * Returns projects for the selected portfolio and all its descendants.
 *
 * Projects with NO portfolio are rolled up under a ROOT portfolio (one with no
 * parent) so they are always reachable somewhere. Without this they matched no
 * scope and were invisible to every user in every mode - the startup wizard
 * writes `portfolioId: selectedPortfolio?.id ?? ''`, so any project created
 * while no portfolio was selected fell straight into that hole (18 such rows
 * were stranded in production). The wizard now refuses to create them, but the
 * roll-up keeps the pre-existing ones - and any future stray - findable.
 */
export function useSelectedPortfolioProjects() {
  const portfolio = useAppStore((s) => s.selectedPortfolio)
  const scope = usePortfolioScope()
  const { data: allProjects, isLoading, ...rest } = useProjects()

  const projects = useMemo(() => {
    if (!allProjects || !portfolio) return []
    const atRoot = !portfolio.parentId
    return allProjects.filter(p =>
      scope.has(p.portfolioId) || (atRoot && isUnassignedToPortfolio(p)),
    )
  }, [allProjects, portfolio, scope])

  return { data: projects, isLoading, ...rest }
}

// ---------------------------------------------------------------------------
// Baseline Management
// ---------------------------------------------------------------------------
export function useBaselineModels(projectId?: string) {
  return useQuery({
    queryKey: ['baselineModels', projectId],
    queryFn: () => dataService.getBaselineModels(projectId),
    enabled: !!projectId,
    staleTime: 2 * 60 * 1000,
  })
}

export function useBaselineWbsSummaries(baselineModelId?: string) {
  return useQuery({
    queryKey: ['baselineWbsSummaries', baselineModelId],
    queryFn: () => dataService.getBaselineWbsSummaries(baselineModelId),
    enabled: !!baselineModelId,
    staleTime: 2 * 60 * 1000,
  })
}

/** Which WBS hierarchy Budget Management cost-plans against (Cost Management WBS by default). */
export function useBudgetWbsSource(projectId?: string) {
  return useQuery({
    queryKey: ['budgetWbsSource', projectId],
    queryFn: () => getBudgetWbsSource(projectId!),
    enabled: !!projectId,
    staleTime: 2 * 60 * 1000,
  })
}

export function useSetBudgetWbsSource(projectId?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (source: BudgetWbsSource) => setBudgetWbsSource(projectId!, source),
    onSuccess: (_d, source) => { qc.setQueryData(['budgetWbsSource', projectId], source) },
  })
}

/** Elements of the hierarchy the budget is planned against. */
export function useBudgetPlanNodes(projectId?: string, source?: BudgetWbsSource) {
  return useQuery({
    queryKey: ['budgetPlanNodes', projectId, source],
    queryFn: () => getBudgetPlanNodes(projectId!, source!),
    enabled: !!projectId && !!source,
    staleTime: 2 * 60 * 1000,
  })
}

/** Save edited WBS budget rows for a baseline (WBS Cost Breakdown "Edit budget"). */
export function useSaveBaselineWbsSummaries(baselineModelId?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (rows: PlanSummary[]) => sb.saveBaselineWbsSummaries(baselineModelId!, rows),
    onSettled: () => { qc.invalidateQueries({ queryKey: ['baselineWbsSummaries', baselineModelId] }) },
  })
}

export function useBaselineTimePhaseEntries(baselineModelId?: string) {
  return useQuery({
    queryKey: ['baselineTimePhaseEntries', baselineModelId],
    queryFn: () => dataService.getBaselineTimePhaseEntries(baselineModelId),
    enabled: !!baselineModelId,
    staleTime: 2 * 60 * 1000,
  })
}

/**
 * ProjectTasks #94b first slice - mutation hook for inline cell
 * editing of baseline_time_phase_entries.total_amount. The supabase
 * function preserves the base/burden split proportionally when the
 * row already has one; otherwise puts the whole new total into
 * baseAmount.
 *
 * ProjectTasks #94b Phase 3 close-out - optimistic update so the
 * matrix totals + Cost & Revenue chart re-render INSTANTLY on edit,
 * not after the server round-trip. Mirrors the server's base/burden
 * ratio preservation locally so the stacked-cost breakdown doesn't
 * briefly flicker. Rolls back on error; invalidates on settle so the
 * authoritative server values land.
 */
export function useUpdateBaselineTimePhaseTotal(baselineModelId?: string) {
  const qc = useQueryClient()
  const queryKey = ['baselineTimePhaseEntries', baselineModelId]
  return useMutation({
    mutationFn: ({ entryId, totalAmount }: { entryId: string; totalAmount: number }) =>
      sb.updateBaselineTimePhaseTotal(entryId, totalAmount),
    onMutate: async ({ entryId, totalAmount }) => {
      await qc.cancelQueries({ queryKey })
      const previous = qc.getQueryData<import('@/types/ppm').BaselineTimePhaseEntry[]>(queryKey)
      if (previous) {
        qc.setQueryData<import('@/types/ppm').BaselineTimePhaseEntry[]>(queryKey, (old) => {
          if (!old) return old
          return old.map(e => {
            if (e.id !== entryId) return e
            const exTotal = (e.baseAmount ?? 0) + (e.burdenAmount ?? 0)
            let nextBase: number, nextBurden: number
            if (exTotal > 0) {
              const ratio = totalAmount / exTotal
              nextBase = (e.baseAmount ?? 0) * ratio
              nextBurden = (e.burdenAmount ?? 0) * ratio
            } else {
              nextBase = totalAmount
              nextBurden = 0
            }
            return { ...e, totalAmount, baseAmount: nextBase, burdenAmount: nextBurden }
          })
        })
      }
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKey, ctx.previous)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey })
    },
  })
}

/**
 * ProjectTasks #94b architectural rework - mutation hook for INSERTING
 * fresh time-phase entries. Used by the inline "+ add" affordance on
 * empty-WBS rows in the Time-Phased View matrix, so users can
 * populate baseline data inline rather than waiting for a seed
 * migration.
 *
 * Optimistic update appends the new entry to the cached list
 * immediately so the matrix subtotal row + chart re-render before
 * the server round-trip lands. Rolls back on error; onSettled
 * invalidates so authoritative server values + server-assigned
 * timestamps land.
 */
export function useCreateBaselineTimePhaseEntry(baselineModelId?: string) {
  const qc = useQueryClient()
  const queryKey = ['baselineTimePhaseEntries', baselineModelId]
  return useMutation({
    mutationFn: (args: Parameters<typeof sb.createBaselineTimePhaseEntry>[0]) =>
      sb.createBaselineTimePhaseEntry(args),
    onMutate: async (args) => {
      await qc.cancelQueries({ queryKey })
      const previous = qc.getQueryData<import('@/types/ppm').BaselineTimePhaseEntry[]>(queryKey)
      if (previous) {
        const nowIso = new Date().toISOString()
        const optimistic: import('@/types/ppm').BaselineTimePhaseEntry = {
          id: args.id,
          baselineModelId: args.baselineModelId,
          wbsElementId: args.wbsElementId,
          wbsElementCode: args.wbsElementCode,
          wbsElementName: args.wbsElementName,
          resourceCategoryId: args.resourceCategoryId,
          resourceCategoryName: args.resourceCategoryName,
          costRevenueType: args.costRevenueType,
          period: args.period,
          baseAmount: args.totalAmount,
          burdenAmount: 0,
          totalAmount: args.totalAmount,
          hours: null,
          createdAt: nowIso,
          updatedAt: nowIso,
        } as unknown as import('@/types/ppm').BaselineTimePhaseEntry
        qc.setQueryData<import('@/types/ppm').BaselineTimePhaseEntry[]>(queryKey, (old) => {
          return old ? [...old, optimistic] : [optimistic]
        })
      }
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKey, ctx.previous)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey })
    },
  })
}

/**
 * ProjectTasks #94c - mutation hook for inline cell editing of
 * revenue_plan_entries.planned_amount. Symmetric to the time-phase
 * write-back so users only learn one interaction pattern.
 *
 * ProjectTasks #94b Phase 3 close-out - optimistic update so the
 * revenue chart + matrix snap to the new value immediately.
 */
export function useUpdateRevenuePlanAmount(baselineModelId?: string) {
  const qc = useQueryClient()
  const queryKey = ['revenuePlanEntries', baselineModelId]
  return useMutation({
    mutationFn: ({ entryId, plannedAmount }: { entryId: string; plannedAmount: number }) =>
      sb.updateRevenuePlanAmount(entryId, plannedAmount),
    onMutate: async ({ entryId, plannedAmount }) => {
      await qc.cancelQueries({ queryKey })
      const previous = qc.getQueryData<Array<{ id: string; plannedAmount: number; [k: string]: unknown }>>(queryKey)
      if (previous) {
        qc.setQueryData<typeof previous>(queryKey, (old) => {
          if (!old) return old
          return old.map(e => e.id === entryId ? { ...e, plannedAmount } : e)
        })
      }
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKey, ctx.previous)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey })
    },
  })
}

export function useBurdenRateDecks(projectId?: string) {
  return useQuery({
    queryKey: ['burdenRateDecks', projectId],
    queryFn: () => dataService.getBurdenRateDecks(projectId),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
  })
}

export function useResourceCategories() {
  return useQuery({
    queryKey: ['resourceCategories'],
    queryFn: dataService.getResourceCategories,
    staleTime: 10 * 60 * 1000,
  })
}

export function useScheduleVersions(projectId?: string) {
  return useQuery({
    queryKey: ['scheduleVersions', projectId],
    queryFn: () => dataService.getScheduleVersions(projectId),
    staleTime: 2 * 60 * 1000,
    enabled: !!projectId,
  })
}

export function useBaselineChangeRequests(projectId?: string, baselineModelId?: string) {
  return useQuery({
    queryKey: ['baselineChangeRequests', projectId, baselineModelId],
    queryFn: () => dataService.getBaselineChangeRequests(projectId, baselineModelId),
    staleTime: 2 * 60 * 1000,
    enabled: !!projectId,
  })
}

export function useRevenuePlanEntries(baselineModelId?: string) {
  return useQuery({
    queryKey: ['revenuePlanEntries', baselineModelId],
    queryFn: () => dataService.getRevenuePlanEntries(baselineModelId),
    enabled: !!baselineModelId,
    staleTime: 2 * 60 * 1000,
  })
}

// ---------------------------------------------------------------------------
// Forecast Management
// ---------------------------------------------------------------------------
export function useProjectForecastConfig(projectId?: string) {
  return useQuery({
    queryKey: ['projectForecastConfig', projectId],
    queryFn: () => dataService.getProjectForecastConfig(projectId),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
  })
}

export function useForecastVersions(projectId?: string) {
  return useQuery({
    queryKey: ['forecastVersions', projectId],
    queryFn: () => dataService.getForecastVersions(projectId),
    enabled: !!projectId,
    staleTime: 2 * 60 * 1000,
  })
}

/**
 * Create a new forecast version (Forecast or ForecastWhatIf). Optimistically
 * prepends it to the cached list, persists via dataService (Supabase in
 * supabase mode, no-op echo in mock mode), and re-syncs on settle.
 */
export function useCreateForecastVersion(projectId?: string) {
  const qc = useQueryClient()
  const queryKey = ['forecastVersions', projectId]
  return useMutation({
    mutationFn: (version: import('@/types/forecasting').ForecastVersion) =>
      dataService.createForecastVersion(version),
    onMutate: async (version) => {
      await qc.cancelQueries({ queryKey })
      const previous = qc.getQueryData<import('@/types/forecasting').ForecastVersion[]>(queryKey)
      qc.setQueryData<import('@/types/forecasting').ForecastVersion[]>(queryKey, (old = []) => [...old, version])
      return { previous }
    },
    onError: (_err, _version, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKey, ctx.previous)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey })
    },
  })
}

// ---------------------------------------------------------------------------
// Rates Management - labor rates, costing-sheet rate decks, deck assignments
// ---------------------------------------------------------------------------
type LaborRate = import('@/types/rates').LaborRateOrgDiscipline
type CostingSheet = import('@/types/rates').CostingSheetVersion
type DeckAssignment = import('@/types/rates').RateDeckAssignment

export function useProjectLaborRates(projectId?: string) {
  return useQuery({
    queryKey: ['projectLaborRates', projectId],
    queryFn: () => dataService.getProjectLaborRates(projectId),
    enabled: !!projectId,
    staleTime: 2 * 60 * 1000,
  })
}

export function useUpsertProjectLaborRate(projectId?: string) {
  const qc = useQueryClient()
  const queryKey = ['projectLaborRates', projectId]
  return useMutation({
    mutationFn: (rate: LaborRate) => dataService.upsertProjectLaborRate(rate),
    onMutate: async (rate) => {
      await qc.cancelQueries({ queryKey })
      const previous = qc.getQueryData<LaborRate[]>(queryKey)
      qc.setQueryData<LaborRate[]>(queryKey, (old = []) => {
        const rest = old.filter(r => r.id !== rate.id)
        return [...rest, rate]
      })
      return { previous }
    },
    onError: (_e, _v, ctx) => { if (ctx?.previous) qc.setQueryData(queryKey, ctx.previous) },
    onSettled: () => qc.invalidateQueries({ queryKey }),
  })
}

export function useDeleteProjectLaborRate(projectId?: string) {
  const qc = useQueryClient()
  const queryKey = ['projectLaborRates', projectId]
  return useMutation({
    mutationFn: (id: string) => dataService.deleteProjectLaborRate(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey })
      const previous = qc.getQueryData<LaborRate[]>(queryKey)
      qc.setQueryData<LaborRate[]>(queryKey, (old = []) => old.filter(r => r.id !== id))
      return { previous }
    },
    onError: (_e, _v, ctx) => { if (ctx?.previous) qc.setQueryData(queryKey, ctx.previous) },
    onSettled: () => qc.invalidateQueries({ queryKey }),
  })
}

export function useProjectRateDecks(projectId?: string) {
  return useQuery({
    queryKey: ['projectRateDecks', projectId],
    queryFn: () => dataService.getProjectRateDecks(projectId),
    enabled: !!projectId,
    staleTime: 2 * 60 * 1000,
  })
}

export function useUpsertProjectRateDeck(projectId?: string) {
  const qc = useQueryClient()
  const queryKey = ['projectRateDecks', projectId]
  return useMutation({
    mutationFn: (version: CostingSheet) => dataService.upsertProjectRateDeck(version),
    onMutate: async (version) => {
      await qc.cancelQueries({ queryKey })
      const previous = qc.getQueryData<CostingSheet[]>(queryKey)
      qc.setQueryData<CostingSheet[]>(queryKey, (old = []) => {
        const rest = old.filter(d => d.id !== version.id)
        return [...rest, version]
      })
      return { previous }
    },
    onError: (_e, _v, ctx) => { if (ctx?.previous) qc.setQueryData(queryKey, ctx.previous) },
    onSettled: () => qc.invalidateQueries({ queryKey }),
  })
}

export function useDeleteProjectRateDeck(projectId?: string) {
  const qc = useQueryClient()
  const queryKey = ['projectRateDecks', projectId]
  return useMutation({
    mutationFn: (id: string) => dataService.deleteProjectRateDeck(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey })
      const previous = qc.getQueryData<CostingSheet[]>(queryKey)
      qc.setQueryData<CostingSheet[]>(queryKey, (old = []) => old.filter(d => d.id !== id))
      return { previous }
    },
    onError: (_e, _v, ctx) => { if (ctx?.previous) qc.setQueryData(queryKey, ctx.previous) },
    onSettled: () => qc.invalidateQueries({ queryKey }),
  })
}

export function useRateDeckAssignments(projectId?: string) {
  return useQuery({
    queryKey: ['rateDeckAssignments', projectId],
    queryFn: () => dataService.getRateDeckAssignments(projectId),
    enabled: !!projectId,
    staleTime: 2 * 60 * 1000,
  })
}

export function useUpsertRateDeckAssignment(projectId?: string) {
  const qc = useQueryClient()
  const queryKey = ['rateDeckAssignments', projectId]
  return useMutation({
    mutationFn: (a: DeckAssignment) => dataService.upsertRateDeckAssignment(a),
    onMutate: async (a) => {
      await qc.cancelQueries({ queryKey })
      const previous = qc.getQueryData<DeckAssignment[]>(queryKey)
      qc.setQueryData<DeckAssignment[]>(queryKey, (old = []) => {
        const rest = old.filter(x => x.id !== a.id)
        return [...rest, a]
      })
      return { previous }
    },
    onError: (_e, _v, ctx) => { if (ctx?.previous) qc.setQueryData(queryKey, ctx.previous) },
    onSettled: () => qc.invalidateQueries({ queryKey }),
  })
}

export function useDeleteRateDeckAssignment(projectId?: string) {
  const qc = useQueryClient()
  const queryKey = ['rateDeckAssignments', projectId]
  return useMutation({
    mutationFn: (id: string) => dataService.deleteRateDeckAssignment(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey })
      const previous = qc.getQueryData<DeckAssignment[]>(queryKey)
      qc.setQueryData<DeckAssignment[]>(queryKey, (old = []) => old.filter(x => x.id !== id))
      return { previous }
    },
    onError: (_e, _v, ctx) => { if (ctx?.previous) qc.setQueryData(queryKey, ctx.previous) },
    onSettled: () => qc.invalidateQueries({ queryKey }),
  })
}

// ---------------------------------------------------------------------------
// CAS / DCAA Rates Management (Organizational Management context).
// Org-wide cost waterfall: segments -> rate pools -> cost pools -> cost records.
// ---------------------------------------------------------------------------
type CasSegmentH = import('@/types/casRates').RateSegment
type CasRatePoolH = import('@/types/casRates').RatePool
type CasCostPoolH = import('@/types/casRates').CostPool

export function useCasRateSegments(orgId?: string) {
  return useQuery({
    queryKey: ['casRateSegments', orgId ?? 'all'],
    queryFn: () => dataService.getCasRateSegments(orgId),
    staleTime: 2 * 60 * 1000,
  })
}

export function useCasRatePools(orgId?: string) {
  return useQuery({
    queryKey: ['casRatePools', orgId ?? 'all'],
    queryFn: () => dataService.getCasRatePools(orgId),
    staleTime: 2 * 60 * 1000,
  })
}

export function useCasCostPools(orgId?: string) {
  return useQuery({
    queryKey: ['casCostPools', orgId ?? 'all'],
    queryFn: () => dataService.getCasCostPools(orgId),
    staleTime: 2 * 60 * 1000,
  })
}

export function useCasCostRecords(orgId?: string) {
  return useQuery({
    queryKey: ['casCostRecords', orgId ?? 'all'],
    queryFn: () => dataService.getCasCostRecords(orgId),
    staleTime: 2 * 60 * 1000,
  })
}

export function useUpsertCasRateSegment(orgId?: string) {
  const qc = useQueryClient()
  const queryKey = ['casRateSegments', orgId ?? 'all']
  return useMutation({
    mutationFn: (s: CasSegmentH) => dataService.upsertCasRateSegment(s),
    onMutate: async (s) => {
      await qc.cancelQueries({ queryKey })
      const previous = qc.getQueryData<CasSegmentH[]>(queryKey)
      qc.setQueryData<CasSegmentH[]>(queryKey, (old = []) => [...old.filter((x) => x.id !== s.id), s])
      return { previous }
    },
    onError: (_e, _v, ctx) => { if (ctx?.previous) qc.setQueryData(queryKey, ctx.previous) },
    onSettled: () => qc.invalidateQueries({ queryKey }),
  })
}

export function useUpsertCasRatePool(orgId?: string) {
  const qc = useQueryClient()
  const queryKey = ['casRatePools', orgId ?? 'all']
  return useMutation({
    mutationFn: (p: CasRatePoolH) => dataService.upsertCasRatePool(p),
    onMutate: async (p) => {
      await qc.cancelQueries({ queryKey })
      const previous = qc.getQueryData<CasRatePoolH[]>(queryKey)
      qc.setQueryData<CasRatePoolH[]>(queryKey, (old = []) => [...old.filter((x) => x.id !== p.id), p])
      return { previous }
    },
    onError: (_e, _v, ctx) => { if (ctx?.previous) qc.setQueryData(queryKey, ctx.previous) },
    onSettled: () => qc.invalidateQueries({ queryKey }),
  })
}

export function useUpsertCasCostPool(orgId?: string) {
  const qc = useQueryClient()
  const queryKey = ['casCostPools', orgId ?? 'all']
  return useMutation({
    mutationFn: (p: CasCostPoolH) => dataService.upsertCasCostPool(p),
    onMutate: async (p) => {
      await qc.cancelQueries({ queryKey })
      const previous = qc.getQueryData<CasCostPoolH[]>(queryKey)
      qc.setQueryData<CasCostPoolH[]>(queryKey, (old = []) => [...old.filter((x) => x.id !== p.id), p])
      return { previous }
    },
    onError: (_e, _v, ctx) => { if (ctx?.previous) qc.setQueryData(queryKey, ctx.previous) },
    onSettled: () => qc.invalidateQueries({ queryKey }),
  })
}

export function useProjectLoadedResources(projectId?: string) {
  return useQuery({
    queryKey: ['projectLoadedResources', projectId],
    queryFn: () => dataService.getProjectLoadedResources(projectId!),
    enabled: !!projectId,
    staleTime: 2 * 60 * 1000,
  })
}

export function useForecastWbsSummaries(forecastVersionId?: string) {
  return useQuery({
    queryKey: ['forecastWbsSummaries', forecastVersionId],
    queryFn: () => dataService.getForecastWbsSummaries(forecastVersionId),
    enabled: !!forecastVersionId,
    staleTime: 2 * 60 * 1000,
  })
}

export function useForecastResourceLines(forecastVersionId?: string, costNodeId?: string) {
  return useQuery({
    queryKey: ['forecastResourceLines', forecastVersionId, costNodeId],
    queryFn: () => dataService.getForecastResourceLines(forecastVersionId, costNodeId),
    enabled: !!forecastVersionId,
    staleTime: 2 * 60 * 1000,
  })
}

export function useForecastTimePhaseEntries(forecastVersionId?: string, costNodeId?: string) {
  return useQuery({
    queryKey: ['forecastTimePhaseEntries', forecastVersionId, costNodeId],
    queryFn: () => dataService.getForecastTimePhaseEntries(forecastVersionId, costNodeId),
    enabled: !!forecastVersionId,
    staleTime: 2 * 60 * 1000,
  })
}

/** SAP-imported actuals (forecast_actuals) for a version. Supabase-direct: this
 *  is a live-SAP feature with no mock path. See forecastActualsService. */
export function useForecastActuals(forecastVersionId?: string) {
  return useQuery({
    queryKey: ['forecastActuals', forecastVersionId],
    queryFn: () => sb.getForecastActuals(forecastVersionId),
    enabled: !!forecastVersionId,
    staleTime: 2 * 60 * 1000,
  })
}

export function useRevenueForecastLines(forecastVersionId?: string, costNodeId?: string) {
  return useQuery({
    queryKey: ['revenueForecastLines', forecastVersionId, costNodeId],
    queryFn: () => dataService.getRevenueForecastLines(forecastVersionId, costNodeId),
    enabled: !!forecastVersionId,
    staleTime: 2 * 60 * 1000,
  })
}

export function useRevenueTimePhaseEntries(forecastVersionId?: string, costNodeId?: string) {
  return useQuery({
    queryKey: ['revenueTimePhaseEntries', forecastVersionId, costNodeId],
    queryFn: () => dataService.getRevenueTimePhaseEntries(forecastVersionId, costNodeId),
    enabled: !!forecastVersionId,
    staleTime: 2 * 60 * 1000,
  })
}

export function useForecastSubmissions(forecastVersionId?: string) {
  return useQuery({
    queryKey: ['forecastSubmissions', forecastVersionId],
    queryFn: () => dataService.getForecastSubmissions(forecastVersionId),
    enabled: !!forecastVersionId,
    staleTime: 2 * 60 * 1000,
  })
}

export function useMaterialForecastLines(forecastVersionId?: string) {
  return useQuery({
    queryKey: ['materialForecastLines', forecastVersionId],
    queryFn: () => dataService.getMaterialForecastLines(forecastVersionId),
    enabled: !!forecastVersionId,
    staleTime: 2 * 60 * 1000,
  })
}

export function useOdcForecastLines(forecastVersionId?: string) {
  return useQuery({
    queryKey: ['odcForecastLines', forecastVersionId],
    queryFn: () => dataService.getOdcForecastLines(forecastVersionId),
    enabled: !!forecastVersionId,
    staleTime: 2 * 60 * 1000,
  })
}

type MaterialLine = import('@/types/forecasting').MaterialForecastLine
type OdcLine = import('@/types/forecasting').OdcForecastLine

// The Material/ODC tabs hold edited rows in local state and autosave them, so
// these mutations just persist (no optimistic cache writes - those would refire
// the tab's server-sync effect and fight the local edit buffer).
export function useUpsertMaterialForecastLine() {
  return useMutation({ mutationFn: (line: MaterialLine) => dataService.upsertMaterialForecastLine(line) })
}
export function useDeleteMaterialForecastLine() {
  return useMutation({ mutationFn: (id: string) => dataService.deleteMaterialForecastLine(id) })
}
export function useUpsertOdcForecastLine() {
  return useMutation({ mutationFn: (line: OdcLine) => dataService.upsertOdcForecastLine(line) })
}
export function useDeleteOdcForecastLine() {
  return useMutation({ mutationFn: (id: string) => dataService.deleteOdcForecastLine(id) })
}

export function useOdcCategories() {
  return useQuery({
    queryKey: ['odcCategories'],
    queryFn: dataService.getOdcCategories,
    staleTime: 10 * 60 * 1000,
  })
}

/**
 * The G/L / cost-element accounts a project may plan against, resolved
 * from the Chart of Accounts assigned to the project (and narrowed to
 * its scope sub-group, if set). Feeds the GL-account pickers on the
 * Material and ODC forecast tabs. Returns an empty account list when no
 * chart is assigned, so the tabs degrade gracefully to free-text planning.
 */
export function useProjectCoaAccounts(projectId?: string) {
  return useQuery({
    queryKey: ['projectCoaAccounts', projectId],
    queryFn: () => getProjectCoaAccounts(projectId as string),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
  })
}

export function useCapitalAssetForecastLines(forecastVersionId?: string) {
  return useQuery({
    queryKey: ['capitalAssetForecastLines', forecastVersionId],
    queryFn: () => dataService.getCapitalAssetForecastLines(forecastVersionId),
    enabled: !!forecastVersionId,
    staleTime: 2 * 60 * 1000,
  })
}

// ─── Lessons Learned (gap-list #15) ──────────────────────────────────────────

export function useLessonsLearned(projectId?: string) {
  return useQuery({
    queryKey: ['lessonsLearned', projectId],
    queryFn: () => dataService.getLessonsLearned(projectId),
    enabled: !!projectId,
    staleTime: 2 * 60 * 1000,
  })
}

export function useCreateLessonLearned(projectId?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (lesson: import('@/types/lessonsLearned').LessonLearned) =>
      dataService.createLessonLearned(lesson),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['lessonsLearned', projectId] }),
  })
}

export function useWorkAuthorizations(projectId?: string) {
  return useQuery({
    queryKey: ['workAuthorizations', projectId],
    queryFn: () => dataService.getWorkAuthorizations(projectId),
    enabled: !!projectId,
    staleTime: 2 * 60 * 1000,
  })
}

// ─── Work Authorization staffing (migration 00342) ───────────────────────────
// The staffing panel surfaces the latest resource-plan demand slots and lets a
// planner assign discipline-matched named resources. An assign writes BOTH to
// the resource plan line (so the Resources grid stays in sync) and to a
// work_authorization_assignments record (the authorization act).

export function useDemandStaffingModel(projectId?: string) {
  return useQuery({
    queryKey: ['demandStaffing', projectId],
    queryFn: () => import('@/components/forecasting/resourceDemandStaffing').then(m => m.loadDemandStaffingModel(projectId!)),
    enabled: !!projectId,
    staleTime: 60 * 1000,
  })
}

export function useNamedResourceRoster() {
  return useQuery({
    queryKey: ['namedResourceRoster'],
    queryFn: () => import('@/components/forecasting/resourceDemandStaffing').then(m => m.loadNamedResourceRoster()),
    staleTime: 5 * 60 * 1000,
  })
}

export function useWorkAuthorizationAssignments(projectId?: string) {
  return useQuery({
    queryKey: ['workAuthorizationAssignments', projectId],
    queryFn: () => dataService.getWorkAuthorizationAssignments(projectId),
    enabled: !!projectId,
    staleTime: 60 * 1000,
  })
}

export interface StaffDemandSlotInput {
  /** The full plan line being staffed (patched + re-saved). */
  line: import('@/modules/resource-planning/types').PlanLine
  slot: import('@/types/forecasting').ResourceDemandSlot
  /** The resource to assign, or null to unassign (revert to generic). */
  resource: import('@/modules/resource-planning/types').NamedResource | null
  actingUserId: string
  actingUserName: string
}

/**
 * Staff (or unstaff) a demand slot. Writes the plan line back via the resource
 * module's write service AND upserts/deletes the work-authorization assignment
 * record, then invalidates the staffing model, assignment list, and the plan
 * grid's cached queries so both surfaces reflect the change.
 */
export function useStaffDemandSlot(projectId?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ line, slot, resource, actingUserId, actingUserName }: StaffDemandSlotInput) => {
      const writeSvc = await import('@/modules/resource-planning/services/resourcePlanWriteService')
      const today = new Date().toISOString().slice(0, 10)

      if (resource) {
        const patched: import('@/modules/resource-planning/types').PlanLine = {
          ...line,
          resourceMode: 'named',
          namedResourceId: resource.id,
          // Inherit the person's attributes onto the slot (keep the planned
          // role/level unless the resource carries its own).
          role: resource.role || line.role,
          level: resource.level || line.level,
          workLocation: (resource.workLocation || line.workLocation) as import('@/modules/resource-planning/types').PlanLine['workLocation'],
          clearance: (resource.clearance || line.clearance) as import('@/modules/resource-planning/types').PlanLine['clearance'],
          employmentType: (resource.employmentType || line.employmentType) as import('@/modules/resource-planning/types').PlanLine['employmentType'],
          // The person's own rate (if any) overrides the org+discipline rate.
          costRateOverride: resource.costRateOverride ?? null,
          billRateOverride: resource.billRateOverride ?? null,
          lastModifiedBy: actingUserId,
          lastModifiedDate: today,
        }
        await writeSvc.upsertPlanLines([patched], projectId!)
        const assignment: import('@/types/forecasting').WorkAuthorizationAssignment = {
          id: `waa_${line.id}`,
          projectId: projectId!,
          planLineId: line.id,
          planVersionId: slot.planVersionId,
          workAuthorizationId: slot.workAuthorizationId,
          wbsElementId: slot.wbsNodeId,
          wbsElementCode: slot.wbsElementCode || null,
          wbsElementName: slot.wbsElementName || null,
          disciplineCode: slot.disciplineCode,
          disciplineName: slot.disciplineName,
          role: patched.role,
          level: patched.level,
          namedResourceId: resource.id,
          namedResourceName: resource.name,
          plannedHours: slot.plannedHours,
          status: 'Assigned',
          assignedBy: actingUserId,
          assignedByName: actingUserName,
          assignedDate: new Date().toISOString(),
          notes: null,
        }
        await dataService.upsertWorkAuthorizationAssignment(assignment)
      } else {
        const patched: import('@/modules/resource-planning/types').PlanLine = {
          ...line,
          resourceMode: 'generic',
          namedResourceId: null,
          costRateOverride: null,
          billRateOverride: null,
          lastModifiedBy: actingUserId,
          lastModifiedDate: today,
        }
        await writeSvc.upsertPlanLines([patched], projectId!)
        await dataService.deleteWorkAuthorizationAssignmentByLine(line.id)
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['demandStaffing', projectId] })
      qc.invalidateQueries({ queryKey: ['workAuthorizationAssignments', projectId] })
      qc.invalidateQueries({ queryKey: ['workAuthorizations', projectId] })
      qc.invalidateQueries({ queryKey: ['planLines', projectId] })
    },
  })
}

export function useEacCycles(projectId?: string) {
  return useQuery({
    queryKey: ['eacCycles', projectId],
    queryFn: () => dataService.getEacCycles(projectId),
    enabled: !!projectId,
    staleTime: 2 * 60 * 1000,
  })
}

export function useEacSubmissionsByProject(projectId?: string) {
  return useQuery({
    queryKey: ['eacSubmissionsByProject', projectId],
    queryFn: () => dataService.getEacSubmissionsByProject(projectId!),
    enabled: !!projectId,
    staleTime: 2 * 60 * 1000,
  })
}

// ─── EAC workflow mutations ──────────────────────────────────────────────────
// All four invalidate the project-scoped submission query so the cycle list
// + per-submission cards re-render with the new status / approval-chain row.

function invalidateEacQueries(qc: ReturnType<typeof useQueryClient>, projectId?: string) {
  qc.invalidateQueries({ queryKey: ['eacSubmissionsByProject', projectId] })
  qc.invalidateQueries({ queryKey: ['eacCycles', projectId] })
  // Legacy JSONB path - invalidate too in case anything still reads it.
  qc.invalidateQueries({ queryKey: ['forecastSubmissions'] })
}

export function useSubmitEacSubmission(projectId?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      submissionId, submittedBy, submittedByName,
    }: { submissionId: string; submittedBy: string; submittedByName: string }) =>
      dataService.submitEacSubmission(submissionId, submittedBy, submittedByName),
    onSuccess: () => invalidateEacQueries(qc, projectId),
  })
}

export function useApproveEacApprovalStep(projectId?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      approvalId, submissionId, isLastStep, comments,
    }: { approvalId: string; submissionId: string; isLastStep: boolean; comments?: string }) =>
      dataService.approveEacApprovalStep(approvalId, submissionId, isLastStep, comments),
    onSuccess: () => invalidateEacQueries(qc, projectId),
  })
}

export function useRejectEacApprovalStep(projectId?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      approvalId, submissionId, comments,
    }: { approvalId: string; submissionId: string; comments?: string }) =>
      dataService.rejectEacApprovalStep(approvalId, submissionId, comments),
    onSuccess: () => invalidateEacQueries(qc, projectId),
  })
}

export function useRequestEacRevision(projectId?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      approvalId, submissionId, comments,
    }: { approvalId: string; submissionId: string; comments?: string }) =>
      dataService.requestEacRevision(approvalId, submissionId, comments),
    onSuccess: () => invalidateEacQueries(qc, projectId),
  })
}

export function useActualsSyncRecords(forecastVersionId?: string) {
  return useQuery({
    queryKey: ['actualsSyncRecords', forecastVersionId],
    queryFn: () => dataService.getActualsSyncRecords(forecastVersionId),
    enabled: !!forecastVersionId,
    staleTime: 5 * 60 * 1000,
  })
}

export function useDashboardKpis() {
  const portfolio = useAppStore((s) => s.selectedPortfolio)
  const { data: projects, isLoading } = useSelectedPortfolioProjects()

  if (isLoading || !projects) {
    return { isLoading, kpis: null }
  }

  const traditional = projects.filter(p => p.schedulingMethod === 'traditional')
  const agile = projects.filter(p => p.schedulingMethod === 'agile')

  // Current-fiscal-year budget/spent (falls back to lifetime if no FY breakdown)
  const currentFy = `FY${new Date().getFullYear()}`
  const hasAnyFyBudget = projects.some(p => (p.fiscalYearBudgets ?? []).some(b => b.fiscalYear === currentFy))
  const totalBudget = hasAnyFyBudget
    ? projects.reduce((sum, p) => {
        const fy = (p.fiscalYearBudgets ?? []).find(b => b.fiscalYear === currentFy)
        return sum + (fy?.baseline ?? 0)
      }, 0)
    : projects.reduce((sum, p) => sum + p.budget.total, 0)
  const totalSpent = hasAnyFyBudget
    ? projects.reduce((sum, p) => {
        const fy = (p.fiscalYearBudgets ?? []).find(b => b.fiscalYear === currentFy)
        return sum + (fy?.actuals ?? 0)
      }, 0)
    : projects.reduce((sum, p) => sum + p.budget.spent, 0)
  const totalLifetimeBudget = projects.reduce((sum, p) => sum + p.budget.total, 0)
  const totalLifetimeSpent = projects.reduce((sum, p) => sum + p.budget.spent, 0)

  // Traditional EVM metrics (only meaningful for traditional projects)
  const avgSpi = traditional.length > 0
    ? traditional.reduce((sum, p) => sum + p.schedule.spi, 0) / traditional.length
    : 0
  const avgCpi = traditional.length > 0
    ? traditional.reduce((sum, p) => sum + p.schedule.cpi, 0) / traditional.length
    : 0

  // Agile metrics (aggregated across agile projects)
  const avgVelocity = agile.length > 0
    ? Math.round(agile.reduce((sum, p) => sum + (p.agileMetrics?.velocity ?? 0), 0) / agile.length)
    : 0
  const totalCompletedPoints = agile.reduce((sum, p) => sum + (p.agileMetrics?.completedPoints ?? 0), 0)
  const totalPlannedPoints = agile.reduce((sum, p) => sum + (p.agileMetrics?.plannedPoints ?? 0), 0)

  const atRisk = projects.filter(p => p.healthStatus === 'Yellow' || p.healthStatus === 'Red').length
  const onTrack = projects.filter(p => p.healthStatus === 'Green').length

  return {
    isLoading,
    kpis: {
      totalProjects: projects.length,
      traditionalCount: traditional.length,
      agileCount: agile.length,
      totalBudget,
      totalSpent,
      totalLifetimeBudget,
      totalLifetimeSpent,
      currentFiscalYear: currentFy,
      isFiscalYearBudget: hasAnyFyBudget,
      budgetUtilization: totalBudget > 0 ? (totalSpent / totalBudget) * 100 : 0,
      avgSpi: Math.round(avgSpi * 100) / 100,
      avgCpi: Math.round(avgCpi * 100) / 100,
      avgVelocity,
      totalCompletedPoints,
      totalPlannedPoints,
      pointsCompletionRate: totalPlannedPoints > 0 ? Math.round((totalCompletedPoints / totalPlannedPoints) * 100) : 0,
      atRisk,
      onTrack,
      portfolioHealth: portfolio?.healthStatus ?? 'Green',
    },
  }
}

// ---------------------------------------------------------------------------
// Cost Management Studio - universal ledger
// Ledger reads are scoped by fiscal year / period, so the query key carries the
// whole scope. A 5-minute stale time is right: the mirror only changes on a
// sync or a run release, and both invalidate explicitly.
// ---------------------------------------------------------------------------
export function useCmLedgerLines(q: import('@/types/costManagement').LedgerQuery = {}) {
  return useQuery({
    queryKey: ['cmLedgerLines', q],
    queryFn: () => dataService.getCmLedgerLines(q),
    staleTime: 5 * 60 * 1000,
  })
}

export function useCmLedgerSyncs(orgId?: string) {
  return useQuery({
    queryKey: ['cmLedgerSyncs', orgId ?? 'all'],
    queryFn: () => dataService.getCmLedgerSyncs(orgId),
    staleTime: 60 * 1000,
  })
}

export function useCmCoObjects(orgId?: string) {
  return useQuery({
    queryKey: ['cmCoObjects', orgId ?? 'all'],
    queryFn: () => dataService.getCmCoObjects(orgId),
    staleTime: 5 * 60 * 1000,
  })
}

// ---------------------------------------------------------------------------
// Cost Management Studio - pool membership + rate segment rules
// ---------------------------------------------------------------------------
export function useCasSegmentRules(orgId?: string) {
  return useQuery({
    queryKey: ['casSegmentRules', orgId ?? 'all'],
    queryFn: () => dataService.getCasSegmentRules(orgId),
    staleTime: 2 * 60 * 1000,
  })
}

export function useCasObjectAssignments(orgId?: string) {
  return useQuery({
    queryKey: ['casObjectAssignments', orgId ?? 'all'],
    queryFn: () => dataService.getCasObjectAssignments(orgId),
    staleTime: 2 * 60 * 1000,
  })
}

// ---------------------------------------------------------------------------
// Cost Management Studio - allocations
// ---------------------------------------------------------------------------
export function useCmAllocationRules(orgId?: string) {
  return useQuery({
    queryKey: ['cmAllocationRules', orgId ?? 'all'],
    queryFn: () => dataService.getCmAllocationRules(orgId),
    staleTime: 2 * 60 * 1000,
  })
}

export function useCmAllocationRuns(orgId?: string) {
  return useQuery({
    queryKey: ['cmAllocationRuns', orgId ?? 'all'],
    queryFn: () => dataService.getCmAllocationRuns(orgId),
    staleTime: 30 * 1000,
  })
}

// ---------------------------------------------------------------------------
// Cost Management Studio - burden rates
// ---------------------------------------------------------------------------
export function useCmBurdenBases(orgId?: string) {
  return useQuery({
    queryKey: ['cmBurdenBases', orgId ?? 'all'],
    queryFn: () => dataService.getCmBurdenBases(orgId),
    staleTime: 2 * 60 * 1000,
  })
}

export function useCmBurdenDefs(orgId?: string) {
  return useQuery({
    queryKey: ['cmBurdenDefs', orgId ?? 'all'],
    queryFn: () => dataService.getCmBurdenDefs(orgId),
    staleTime: 2 * 60 * 1000,
  })
}

export function useCmBurdenRuns(orgId?: string) {
  return useQuery({
    queryKey: ['cmBurdenRuns', orgId ?? 'all'],
    queryFn: () => dataService.getCmBurdenRuns(orgId),
    staleTime: 30 * 1000,
  })
}

// ---------------------------------------------------------------------------
// Cost Management Studio - cost transfers
// ---------------------------------------------------------------------------
export function useCmTransfers(orgId?: string) {
  return useQuery({
    queryKey: ['cmTransfers', orgId ?? 'all'],
    queryFn: () => dataService.getCmTransfers(orgId),
    staleTime: 30 * 1000,
  })
}

// ---------------------------------------------------------------------------
// Cost Management Studio - incurred cost reporting
// ---------------------------------------------------------------------------
export function useCmIcrSubmissions(orgId?: string) {
  return useQuery({
    queryKey: ['cmIcrSubmissions', orgId ?? 'all'],
    queryFn: () => dataService.getCmIcrSubmissions(orgId),
    staleTime: 60 * 1000,
  })
}

export function useCmIcrInputs(orgId?: string) {
  return useQuery({
    queryKey: ['cmIcrInputs', orgId ?? 'all'],
    queryFn: () => dataService.getCmIcrInputs(orgId),
    staleTime: 60 * 1000,
  })
}
